import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild, effect, input, output, signal } from "@angular/core"

import { HDImage, HotelDirectory } from "@api/communication/models/hotel-directory"
import { InputValue } from "@api/communication/models/template"
import { MediaLibraryCleanupService } from "@shared/services/media-library-cleanup.service"
import { UtilityApiService } from "@api/utility/utility-api.service"
import { IconImageService } from "@shared/services/icon-image.service"
import { StateService } from "@shared/services/state.service"
import { HotelDirectoryService } from "app/hotel-directory/hotel-directory.service"
import { Environment } from "environments/environment"
import { v4 as uuidv4 } from "uuid"
import { lastValueFrom } from "rxjs"

interface RenderedCrop {
  blob: Blob
  imageType: string
  fileExtension: string
  crop: { left: number; top: number; right: number; bottom: number }
  width: number
  height: number
}

@Component({
  selector: "ag-adjust-image",
  templateUrl: "./ag-adjust-image.component.html",
  styleUrls: ["./ag-adjust-image.component.scss"],
  standalone: false,
})
export class AgAdjustImageComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() image: any
  @Input() isEmailBuilder: boolean = false
  @Input() emailBuilderId: string = ""
  @Input() emailBuilderName: string = ""
  @Input() layoutHeight: number = 0
  @Input() allowFillOption: boolean = true
  @Input() lockedAspectRatio: number | null = null
  @Input() fromHD: boolean = false
  clickedOutside = input<boolean>(false)
  clickedOutsidePulse = input<number>(0)
  dismissClickedOutside = output<void>()
  saveError = signal<string | null>(null)
  private saveErrorPulse = signal<number>(0)

  @ViewChild("taskbarEl") private taskbarEl: ElementRef<HTMLElement>
  @ViewChild("toasterEl") private toasterEl: ElementRef<HTMLElement>
  @ViewChild("infoToasterEl") private infoToasterEl: ElementRef<HTMLElement>

  @Output() saved = new EventEmitter<HDImage>()
  @Output() cancelled = new EventEmitter<void>()

  imageEl: ElementRef
  private initDone = false
  @ViewChild("imageEl") set imageElRef(el: ElementRef) {
    if (!el || this.initDone) return
    this.imageEl = el
    this.initDone = true
    const imgEl = el.nativeElement as HTMLImageElement
    if (imgEl.complete) {
      this.initFromRenderedImage(imgEl)
    } else {
      imgEl.onload = () => this.initFromRenderedImage(imgEl)
    }
  }

  imageFit: "Original" | "Fill" | "Crop" = "Original"
  imageRotation: 0 | 90 | 180 | 270 = 0
  cropShape: "Freeform" | "Square" | "Original" | "4:3" | "16:9" | "2:1" = "Freeform"
  private originalAspectRatio: number = 1
  cropAligment: string = "Vertical"
  uploading: boolean = false
  cropBox = { x: 0, y: 0, w: 0, h: 0 }
  fillDragX: number = 0
  fillDragY: number = 0
  isFillDragging: boolean = false
  isFitTransitioning: boolean = false
  isRestoring: boolean = false
  isHoveringImage: boolean = false
  isHoveringCropBox: boolean = false
  // DOM size of the <img> before CSS rotation for Fill + 90°/270°. Null otherwise.
  fillImgSize: { w: number; h: number } | null = null

  sizeInputs: InputValue[] = [
    { direction: "Width", value: 0 },
    { direction: "Height", value: 0 },
  ]

  protected workingImage: any
  private savedInlineStyle: any
  private renderedWidth: number = 0
  private renderedHeight: number = 0
  private hotelId: string = ""
  private imageSrc: string = ""
  private originalImageLink: string = ""
  private activeCropHandle: "top" | "bottom" | "left" | "right" | "topleft" | "topright" | "bottomleft" | "bottomright" | "move" | null = null
  private cropDragStart: { x: number; y: number } | null = null
  private fillDragStart: { x: number; y: number } | null = null
  private readonly minCropPx = 20
  private originalWidth: string = ""
  private originalHeight: string = ""
  private originalImagefit: "Original" | "Fill" | "Crop" = "Original"
  private baseLayoutMax: number = 600
  rotationClip: { w: number; h: number } | null = null

  private readonly EMAIL_BUILDER_CROP_PREVIEW_MIN = 600
  private readonly HD_CROP_PREVIEW_MIN = 700
  private readonly JPEG_QUALITY = 0.85
  private readonly MEANINGFUL_ALPHA_RATIO = 0.01
  private readonly MAX_OUTPUT_SIDE = 2560
  private static readonly folderIdCache = new Map<string, string>()
  private sourceImagePromise: Promise<HTMLImageElement> | null = null

  private get cropPreviewMultiplier(): number {
    if (this.imageFit !== "Crop") return 1
    const target = this.fromHD ? this.HD_CROP_PREVIEW_MIN : this.EMAIL_BUILDER_CROP_PREVIEW_MIN
    const base = this.baseLayoutMax || 600
    if (base >= target) return 1
    return target / base
  }

  get isCropPreviewWidened(): boolean {
    return this.cropPreviewMultiplier > 1
  }

  get cropPreviewWidenedWidth(): number {
    const base = this.baseLayoutMax || 600
    return base * this.cropPreviewMultiplier
  }

  constructor(
    private stateService: StateService,
    public iconService: IconImageService,
    private utilityApiService: UtilityApiService,
    private hdService: HotelDirectoryService,
    private hostEl: ElementRef<HTMLElement>,
    private mediaLibraryCleanupService: MediaLibraryCleanupService,
  ) {
    effect(() => {
      this.clickedOutsidePulse()
      this.saveErrorPulse()
      const visible = this.clickedOutside() || this.saveError() !== null
      const el = this.toasterEl?.nativeElement
      if (!visible || !el) return
      el.classList.remove("jiggle")
      void el.offsetWidth
      el.classList.add("jiggle")
    })

    effect(
      () => {
        if (this.clickedOutside()) this.saveError.set(null)
      },
      { allowSignalWrites: true },
    )
  }

  cropPreviewCenterShiftPx: number = 0

  private recomputeCropPreviewCenterShift(): void {
    if (!this.isCropPreviewWidened || !this.hostEl?.nativeElement) {
      this.cropPreviewCenterShiftPx = 0
      return
    }
    const hostRect = this.hostEl.nativeElement.getBoundingClientRect()
    const viewportCenterX = document.documentElement.clientWidth / 2
    const currentCenterX = hostRect.left + this.cropPreviewWidenedWidth / 2
    this.cropPreviewCenterShiftPx = Math.round(viewportCenterX - currentCenterX)
  }

  @HostListener("window:resize")
  onWindowResize(): void {
    this.recomputeCropPreviewCenterShift()
  }

  @HostListener("click", ["$event"])
  onHostClick(event: MouseEvent): void {
    event.stopPropagation()
  }

  ngAfterViewInit(): void {
    document.body.appendChild(this.taskbarEl.nativeElement)
    document.body.appendChild(this.toasterEl.nativeElement)
    document.body.appendChild(this.infoToasterEl.nativeElement)
  }

  ngOnDestroy(): void {
    this.taskbarEl?.nativeElement?.remove()
    this.toasterEl?.nativeElement?.remove()
    this.infoToasterEl?.nativeElement?.remove()
  }

  ngOnInit(): void {
    this.isRestoring = true
    this.workingImage = JSON.parse(JSON.stringify(this.image))
    this.savedInlineStyle = JSON.parse(JSON.stringify(this.image.inlineStyle))
    this.hotelId = this.stateService.selectedHotel.Id
    const style = this.workingImage?.inlineStyle
    if (style && this.fromHD) {
      const hasPerCornerRadii = style.ImageBorderRadiusTopLeft != null || style.ImageBorderRadiusTopRight != null || style.ImageBorderRadiusBottomLeft != null || style.ImageBorderRadiusBottomRight != null
      if (!hasPerCornerRadii) {
        style.ImageBorderRadiusTopLeft = "16px"
        style.ImageBorderRadiusTopRight = "16px"
        style.ImageBorderRadiusBottomLeft = "16px"
        style.ImageBorderRadiusBottomRight = "16px"
      }
    }
    if (style) {
      this.originalWidth = style.width || ""
      this.originalHeight = style.height || ""
      const existingLeft = parseFloat(style.marginLeft) || 0
      const existingRight = parseFloat(style.marginRight) || 0
      this.baseLayoutMax = (parseFloat(style.layoutMax) || 600) + existingLeft + existingRight
    }

    const rawCropping = this.workingImage?.imgCropping ?? this.workingImage?.imageCropping
    const cropping = rawCropping ? (typeof rawCropping === "string" ? JSON.parse(rawCropping) : rawCropping) : null
    this.originalImageLink = cropping?.oldImageLink ?? this.workingImage?.img
    if (cropping?.imageFit === "Crop" && cropping.oldImageLink && cropping.originalWidth && cropping.originalHeight) {
      this.imageFit = "Crop"
      this.originalImagefit = "Crop"
      this.cropShape = cropping.cropShape ?? (this.lockedAspectRatio != null ? "Original" : "Freeform")
      this.workingImage.img = cropping.oldImageLink
    }
    if (cropping?.imageRotation != null) {
      this.imageRotation = cropping.imageRotation as 0 | 90 | 180 | 270
    }

    if (!this.allowFillOption && this.imageFit === "Fill") {
      this.imageFit = "Original"
      this.originalImagefit = "Original"
    }

    if (this.lockedAspectRatio != null && !cropping) {
      this.imageFit = "Fill"
      this.originalImagefit = "Fill"
    }
  }

  private initFromRenderedImage(imgEl: HTMLImageElement): void {
    this.renderedWidth = imgEl.clientWidth
    this.renderedHeight = imgEl.clientHeight

    const hdImage = this.buildHDImage()
    const img = new Image()
    img.src = this.originalImageLink || (hdImage.imageCropping?.oldImageLink ?? hdImage.imageLink)
    this.imageSrc = img.src
    this.sourceImagePromise = this.loadSourceImage(this.imageSrc)
    this.sourceImagePromise.catch(() => undefined)
    if (hdImage.imageCropping?.oldImageLink) {
      this.workingImage.img = hdImage.imageCropping.oldImageLink
    }

    img.onload = () => {
      this.originalAspectRatio = img.width / img.height
      if (hdImage.imageCropping?.imageFit) {
        this.imageFit = hdImage.imageCropping.imageFit
        this.originalImagefit = hdImage.imageCropping.imageFit
        if (hdImage.imageCropping.imageFit === "Crop") {
          this.cropShape = hdImage.imageCropping.cropShape ?? (this.lockedAspectRatio != null ? "Original" : "Freeform")
          const style = this.workingImage?.inlineStyle
          const previewWidth = this.baseLayoutMax * this.cropPreviewMultiplier
          if (style) {
            style.width = previewWidth + "px"
            style.height = Math.round(previewWidth / this.originalAspectRatio) + "px"
          }
          this.applyRotationDimensions(this.originalAspectRatio)
          const isTransposed = this.imageRotation === 90 || this.imageRotation === 270
          if (!isTransposed) {
            this.renderedWidth = previewWidth
            this.renderedHeight = Math.round(previewWidth / this.originalAspectRatio)
          }
          const rotImgW = isTransposed ? img.height : img.width
          const rotImgH = isTransposed ? img.width : img.height
          const scaleX = rotImgW / this.renderedWidth
          const scaleY = rotImgH / this.renderedHeight
          const x = hdImage.imageCropping.cropLeft / scaleX
          const y = hdImage.imageCropping.cropTop / scaleY
          const w = Math.max(this.minCropPx, this.renderedWidth - x - hdImage.imageCropping.cropRight / scaleX)
          const h = Math.max(this.minCropPx, this.renderedHeight - y - hdImage.imageCropping.cropBottom / scaleY)
          this.cropBox = { x, y, w, h }
          this.isRestoring = false
          setTimeout(() => this.recomputeCropPreviewCenterShift())
          return
        } else if (hdImage.imageCropping.imageFit === "Fill") {
          const storedRenderedW = hdImage.imageCropping.originalWidth
          const storedRenderedH = hdImage.imageCropping.originalHeight
          const displayW = parseFloat(this.workingImage?.inlineStyle?.width) || storedRenderedW
          const displayH = parseFloat(this.workingImage?.inlineStyle?.height) || storedRenderedH
          if (storedRenderedW && storedRenderedH) {
            this.workingImage.img = hdImage.imageCropping.oldImageLink
            this.renderedWidth = storedRenderedW
            this.renderedHeight = storedRenderedH
            this.sizeInputs[0].value = displayW
            this.sizeInputs[1].value = displayH
            const isTransposed = this.imageRotation === 90 || this.imageRotation === 270
            const rotCanvasW = isTransposed ? img.height : img.width
            const rotCanvasH = isTransposed ? img.width : img.height
            const scaleX = rotCanvasW / storedRenderedW
            const scaleY = rotCanvasH / storedRenderedH
            const maxDragX = Math.max(0, (storedRenderedW - displayW) / 2)
            const maxDragY = Math.max(0, (storedRenderedH - displayH) / 2)
            const rawDragX = maxDragX - hdImage.imageCropping.cropLeft / scaleX
            const rawDragY = maxDragY - hdImage.imageCropping.cropTop / scaleY
            this.fillDragX = Math.max(-maxDragX, Math.min(maxDragX, rawDragX))
            this.fillDragY = Math.max(-maxDragY, Math.min(maxDragY, rawDragY))
            this.applyFillDimensions()
          }
        }
      } else if (this.imageFit === "Fill") {
        this.applyFillDimensions()
      } else {
        this.resetCropToFull()
      }
      if (this.imageRotation === 90 || this.imageRotation === 270) {
        this.applyRotationDimensions()
        if (this.imageFit !== "Crop") {
          this.cropBox = { x: 0, y: 0, w: this.renderedWidth, h: this.renderedHeight }
        }
      }
      this.isRestoring = false
      setTimeout(() => this.recomputeCropPreviewCenterShift())
    }
    img.onerror = () => {
      this.isRestoring = false
    }
  }

  private refreshRenderedSize(): void {
    const imgEl = this.imageEl?.nativeElement as HTMLImageElement
    if (imgEl) {
      const isTransposed = this.imageRotation === 90 || this.imageRotation === 270
      if (isTransposed && this.rotationClip) {
        this.renderedWidth = this.rotationClip.w
        this.renderedHeight = this.rotationClip.h
      } else {
        this.renderedWidth = imgEl.clientWidth
        this.renderedHeight = imgEl.clientHeight
      }
    }
  }

  private resetCropToFull(): void {
    this.refreshRenderedSize()
    this.cropBox = { x: 0, y: 0, w: this.renderedWidth, h: this.renderedHeight }
  }

  private triggerFitTransition(): void {
    this.isFitTransitioning = true
    setTimeout(() => (this.isFitTransitioning = false), 300)
  }

  rotateBy(delta: 90 | -90): void {
    const next = ((this.imageRotation + delta + 360) % 360) as 0 | 90 | 180 | 270
    this.onRotationChange(String(next))
  }

  onRotationChange(value: string): void {
    const deg = parseInt(value, 10)
    if (deg === 0 || deg === 90 || deg === 180 || deg === 270) {
      this.imageRotation = deg
      if (this.imageFit === "Fill") {
        this.fillDragX = 0
        this.fillDragY = 0
      }
      this.applyRotationDimensions()
      this.cropBox = { x: 0, y: 0, w: this.renderedWidth, h: this.renderedHeight }
      if (this.imageFit === "Crop" && !this.fromHD) {
        this.cropShape = "Freeform"
      } else if (this.imageFit === "Crop" && this.fromHD) {
        this.cropShape = "Original"
        this.cropBox = this.centeredBoxForRatio(this.resolveRatio(), this.renderedWidth, this.renderedHeight)
      }
    }
  }

  private applyRotationDimensions(ratioOverride?: number): void {
    const style = this.workingImage?.inlineStyle
    if (!style) return
    const isTransposed = this.imageRotation === 90 || this.imageRotation === 270
    const imgEl = this.imageEl?.nativeElement as HTMLImageElement | undefined
    const naturalRatio = imgEl?.naturalWidth && imgEl?.naturalHeight ? imgEl.naturalWidth / imgEl.naturalHeight : 0
    const baseRatio = ratioOverride || naturalRatio || this.originalAspectRatio || parseFloat(this.workingImage?.imgAspectRatio) || 1
    const layoutMax = this.imageFit === "Crop" ? this.baseLayoutMax * this.cropPreviewMultiplier : (parseFloat(style.layoutMax) || 600) * this.cropPreviewMultiplier

    if (!isTransposed) {
      this.rotationClip = null
      this.fillImgSize = null
      if (this.imageFit === "Original" || this.imageFit === "Crop") {
        const ratio = baseRatio
        style.width = layoutMax + "px"
        style.height = Math.round(layoutMax / ratio) + "px"
        this.renderedWidth = layoutMax
        this.renderedHeight = Math.round(layoutMax / ratio)
      } else if (this.imageFit === "Fill") {
        this.applyFillDimensions()
      }
      return
    }

    if (this.imageFit === "Fill") {
      this.rotationClip = null
      this.applyFillDimensions()
      return
    }

    // Original / Crop 90° or 270°
    this.fillImgSize = null
    const preW = layoutMax
    const preH = Math.round(layoutMax / baseRatio)
    const scale = layoutMax / preH
    const clipW = layoutMax
    const clipH = Math.round(preW * scale)
    const imgW = Math.round(preW * scale)
    const imgH = Math.round(preH * scale)
    style.width = imgW + "px"
    style.height = imgH + "px"
    this.rotationClip = { w: clipW, h: clipH }
    this.renderedWidth = clipW
    this.renderedHeight = clipH
  }

  onImageFitChange(fit: string): void {
    this.triggerFitTransition()
    this.refreshRenderedSize()
    if (fit !== "Fill") {
      this.fillDragX = 0
      this.fillDragY = 0
      this.fillImgSize = null
    }

    if (fit === "Original") {
      this.imageFit = "Original"
      this.originalImagefit ? this.restoreOriginalImage() : this.restoreOriginalSize()
    } else if (fit === "Fill") {
      this.imageFit = "Fill"
      if (this.originalImagefit) {
        this.restoreOriginalImage(() => this.applyFillDimensions())
      } else {
        this.applyFillDimensions()
      }
    } else if (fit === "Crop") {
      const cameFromFill = this.imageFit === "Fill"
      this.imageFit = "Crop"
      this.cropShape = this.lockedAspectRatio != null ? "Original" : "Freeform"
      if (cameFromFill) {
        this.imageRotation = 0
        this.fillDragX = 0
        this.fillDragY = 0
        this.fillImgSize = null
        this.restoreOriginalImage(() => {
          this.applyRotationDimensions(this.originalAspectRatio)
          const setCropBox = () => {
            this.cropBox = this.cropShape === "Freeform" ? { x: 0, y: 0, w: this.renderedWidth, h: this.renderedHeight } : this.centeredBoxForRatio(this.resolveRatio(), this.renderedWidth, this.renderedHeight)
          }
          setCropBox()
          // ag-toggle-button re-emits via ngOnChanges when initValue changes (e.g. 90°→0°),
          // which calls onRotationChange and clobbers cropBox. Re-apply after that pass.
          setTimeout(setCropBox)
        })
      } else if (this.originalImagefit) {
        this.restoreOriginalImage(() => {
          this.applyRotationDimensions(this.originalAspectRatio)
          this.cropBox = { x: 0, y: 0, w: this.renderedWidth, h: this.renderedHeight }
          if (this.cropShape !== "Freeform") {
            this.cropBox = this.centeredBoxForRatio(this.resolveRatio(), this.renderedWidth, this.renderedHeight)
          }
        })
      } else {
        this.restoreOriginalSize()
        this.applyRotationDimensions()
        const w = this.renderedWidth || parseFloat(this.originalWidth) || 0
        const h = this.renderedHeight || parseFloat(this.originalHeight) || 0
        if (w > 0 && h > 0) {
          this.renderedWidth = w
          this.renderedHeight = h
          this.cropBox = { x: 0, y: 0, w, h }
        } else {
          this.resetCropToFull()
        }
        if (this.cropShape !== "Freeform") {
          this.cropBox = this.centeredBoxForRatio(this.resolveRatio(), this.renderedWidth, this.renderedHeight)
        }
      }
    }
    setTimeout(() => this.recomputeCropPreviewCenterShift())
  }

  onCropShapeChange(shape: string): void {
    this.cropShape = shape as typeof this.cropShape
    const W = this.renderedWidth
    const H = this.renderedHeight
    if (this.cropShape === "Freeform") {
      this.cropBox = { x: 0, y: 0, w: W, h: H }
    } else {
      this.cropBox = this.centeredBoxForRatio(this.resolveRatio(), W, H)
    }
  }

  onAlignmentChange(alignment: string): void {
    this.cropAligment = alignment
    if (this.cropShape !== "Freeform") {
      this.cropBox = this.centeredBoxForRatio(this.resolveRatio(), this.renderedWidth, this.renderedHeight)
    }
  }

  private resolveRatio(): number {
    if (this.lockedAspectRatio != null) return this.lockedAspectRatio
    const isHorizontal = this.cropAligment === "Horizontal"
    switch (this.cropShape) {
      case "Original":
        return this.originalAspectRatio
      case "Square":
        return 1
      case "4:3":
        return isHorizontal ? 4 / 3 : 3 / 4
      case "16:9":
        return isHorizontal ? 16 / 9 : 9 / 16
      case "2:1":
        return isHorizontal ? 2 / 1 : 1 / 2
      default:
        return 1
    }
  }

  private centeredBoxForRatio(ratio: number, W: number, H: number): { x: number; y: number; w: number; h: number } {
    let w: number, h: number
    if (W / H >= ratio) {
      h = H
      w = Math.round(H * ratio)
    } else {
      w = W
      h = Math.round(W / ratio)
    }
    return { x: Math.round((W - w) / 2), y: Math.round((H - h) / 2), w, h }
  }

  private restoreOriginalImage(callback?: () => void): void {
    const hdImage = this.buildHDImage()
    const originalLink = this.originalImageLink || (hdImage.imageCropping?.oldImageLink ?? hdImage.imageLink)
    const img = new Image()
    img.onload = () => {
      const style = this.workingImage?.inlineStyle
      if (!style) return
      const maxWidth = parseFloat(style.layoutMax) || 600
      const scaleFactor = img.naturalWidth > maxWidth ? maxWidth / img.naturalWidth : 1
      const newW = Math.round(img.naturalWidth * scaleFactor)
      const newH = Math.round(img.naturalHeight * scaleFactor)
      this.originalAspectRatio = img.naturalWidth / img.naturalHeight
      this.workingImage.img = originalLink
      this.workingImage.imgAspectRatio = this.originalAspectRatio.toString()
      style.width = newW + "px"
      style.height = newH + "px"
      this.sizeInputs[0].value = newW
      this.sizeInputs[1].value = newH
      this.renderedWidth = newW
      this.renderedHeight = newH
      if (this.imageRotation === 90 || this.imageRotation === 270) {
        this.applyRotationDimensions(this.originalAspectRatio)
      }
      if (callback) callback()
    }
    img.src = originalLink
  }

  private restoreOriginalSize(): void {
    const style = this.workingImage?.inlineStyle
    if (!style) return
    const currentMax = parseFloat(style.layoutMax) || this.baseLayoutMax
    const aspectRatio = parseFloat(this.workingImage?.imgAspectRatio) || 1
    const restoredW = parseFloat(this.originalWidth) || 0
    const clampedW = restoredW > currentMax ? currentMax : restoredW
    const clampedH = Math.round(clampedW / aspectRatio)
    style.width = clampedW + "px"
    style.height = clampedH + "px"
    this.sizeInputs[0].value = clampedW
    this.sizeInputs[1].value = clampedH
  }

  private applyFillDimensions(): void {
    const style = this.workingImage?.inlineStyle
    if (!style) return
    const isTransposed = this.imageRotation === 90 || this.imageRotation === 270
    const maxWidth = parseFloat(style.layoutMax) || 600
    // Always use originalAspectRatio — never read imgAspectRatio which may be stale
    const baseRatio = this.originalAspectRatio || 1

    if (isTransposed) {
      const transposedRatio = 1 / baseRatio
      const displayW = maxWidth
      const displayH = this.layoutHeight > 0 ? this.layoutHeight : Math.round(maxWidth / transposedRatio)
      const domH = Math.max(displayW, Math.ceil(displayH / baseRatio))
      const domW = Math.round(domH * baseRatio)
      const visualW = domH
      const visualH = domW

      style.width = displayW + "px"
      style.height = displayH + "px"
      this.sizeInputs[0].value = displayW
      this.sizeInputs[1].value = displayH
      this.renderedWidth = visualW
      this.renderedHeight = visualH
      this.cropBox = { x: (visualW - displayW) / 2, y: (visualH - displayH) / 2, w: displayW, h: displayH }
      this.fillImgSize = { w: domW, h: domH }
      return
    }

    this.fillImgSize = null
    const displayW = maxWidth
    const displayH = this.layoutHeight > 0 ? this.layoutHeight : Math.round(maxWidth / baseRatio)
    const boxRatio = displayW / displayH
    let rendW: number, rendH: number
    if (baseRatio > boxRatio) {
      rendH = displayH
      rendW = Math.round(displayH * baseRatio)
    } else {
      rendW = displayW
      rendH = Math.round(displayW / baseRatio)
    }

    style.width = displayW + "px"
    style.height = displayH + "px"
    this.sizeInputs[0].value = displayW
    this.sizeInputs[1].value = displayH
    this.renderedWidth = rendW
    this.renderedHeight = rendH
    this.cropBox = { x: (rendW - displayW) / 2, y: (rendH - displayH) / 2, w: displayW, h: displayH }
  }

  get rotatedBorderRadius(): { tl: string; tr: string; br: string; bl: string } {
    const s = this.workingImage?.inlineStyle
    const fallback = this.fromHD ? "16px" : "0px"
    const tl = s?.ImageBorderRadiusTopLeft ?? fallback
    const tr = s?.ImageBorderRadiusTopRight ?? fallback
    const br = s?.ImageBorderRadiusBottomRight ?? fallback
    const bl = s?.ImageBorderRadiusBottomLeft ?? fallback
    switch (this.imageRotation) {
      case 90:
        return { tl: bl, tr: tl, br: tr, bl: br }
      case 180:
        return { tl: br, tr: bl, br: tl, bl: tr }
      case 270:
        return { tl: tr, tr: br, br: bl, bl: tl }
      default:
        return { tl, tr, br, bl }
    }
  }

  get fillDragAxis(): "horizontal" | "vertical" | null {
    if (this.imageFit !== "Fill") return null
    return this.renderedWidth > this.sizeInputs[0].value ? "horizontal" : "vertical"
  }

  onFillImageMouseDown(event: MouseEvent): void {
    event.preventDefault()
    this.isFillDragging = true
    this.fillDragStart = { x: event.clientX, y: event.clientY }
  }

  onCropBoxMouseOut(event: MouseEvent, cropBoxEl: HTMLElement): void {
    if (!cropBoxEl.contains(event.relatedTarget as Node)) {
      this.isHoveringCropBox = false
    }
  }

  @HostListener("wheel", ["$event"])
  onWheel(event: WheelEvent): void {
    if ((this.imageFit === "Fill" && !this.isHoveringImage) || (this.imageFit === "Crop" && !this.isHoveringCropBox)) return
    event.preventDefault()

    let d = (event.deltaY !== 0 ? event.deltaY : event.deltaX) * 0.15

    if (this.imageFit === "Crop") {
      const maxX = this.renderedWidth
      const maxY = this.renderedHeight
      d = d * 0.5
      const clampedD = Math.max(this.minCropPx - this.cropBox.w, Math.min(maxX - this.cropBox.x - this.cropBox.w, maxY - this.cropBox.y - this.cropBox.h, d))
      this.cropBox.w += clampedD
      this.cropBox.h += clampedD
      if (this.cropShape !== "Freeform") {
        this.enforceAspectRatio("bottomright", maxX, maxY)
      }
      return
    }

    const axis = this.fillDragAxis
    let dx = 0
    let dy = 0
    if (axis === "horizontal") {
      dx = this.imageRotation === 180 ? d : -d
    } else {
      dy = this.imageRotation === 180 ? d : -d
    }

    const maxDragX = Math.max(0, (this.renderedWidth - this.sizeInputs[0].value) / 2)
    const maxDragY = Math.max(0, (this.renderedHeight - this.sizeInputs[1].value) / 2)
    this.fillDragX = Math.max(-maxDragX, Math.min(maxDragX, this.fillDragX + dx))
    this.fillDragY = Math.max(-maxDragY, Math.min(maxDragY, this.fillDragY + dy))
  }

  onCropBoxDown(event: MouseEvent): void {
    event.preventDefault()
    event.stopPropagation()
    this.activeCropHandle = "move"
    this.cropDragStart = { x: event.clientX, y: event.clientY }
  }

  onCropHandleDown(event: MouseEvent, handle: "top" | "bottom" | "left" | "right" | "topleft" | "topright" | "bottomleft" | "bottomright"): void {
    event.preventDefault()
    event.stopPropagation()
    this.activeCropHandle = handle
    this.cropDragStart = { x: event.clientX, y: event.clientY }
  }

  @HostListener("document:mousemove", ["$event"])
  onMouseMove(event: MouseEvent): void {
    if (this.isFillDragging && this.fillDragStart) {
      const rawDx = event.clientX - this.fillDragStart.x
      const rawDy = event.clientY - this.fillDragStart.y
      this.fillDragStart = { x: event.clientX, y: event.clientY }

      let dx: number, dy: number

      dx = this.imageRotation === 180 ? -rawDx : rawDx
      dy = this.imageRotation === 180 ? -rawDy : rawDy

      const maxDragX = Math.max(0, (this.renderedWidth - this.sizeInputs[0].value) / 2)
      const maxDragY = Math.max(0, (this.renderedHeight - this.sizeInputs[1].value) / 2)
      this.fillDragX = Math.max(-maxDragX, Math.min(maxDragX, this.fillDragX + dx))
      this.fillDragY = Math.max(-maxDragY, Math.min(maxDragY, this.fillDragY + dy))
      return
    }

    if (!this.activeCropHandle || !this.cropDragStart) return

    const dx = event.clientX - this.cropDragStart.x
    const dy = event.clientY - this.cropDragStart.y
    this.cropDragStart = { x: event.clientX, y: event.clientY }

    const maxX = this.renderedWidth
    const maxY = this.renderedHeight

    const isCornerHandle = ["topleft", "topright", "bottomleft", "bottomright"].includes(this.activeCropHandle)
    const isSideHandle = ["top", "bottom", "left", "right"].includes(this.activeCropHandle)

    if (isSideHandle && this.cropShape !== "Freeform") return

    switch (this.activeCropHandle) {
      case "move": {
        this.cropBox.x = Math.max(0, Math.min(maxX - this.cropBox.w, this.cropBox.x + dx))
        this.cropBox.y = Math.max(0, Math.min(maxY - this.cropBox.h, this.cropBox.y + dy))
        break
      }
      case "top": {
        const newY = this.cropBox.y + dy
        const newH = this.cropBox.h - dy
        if (newY >= 0 && newH >= this.minCropPx) {
          this.cropBox.y = newY
          this.cropBox.h = newH
        }
        break
      }
      case "bottom": {
        const newH = this.cropBox.h + dy
        if (newH >= this.minCropPx && this.cropBox.y + newH <= maxY) {
          this.cropBox.h = newH
        }
        break
      }
      case "left": {
        const newX = this.cropBox.x + dx
        const newW = this.cropBox.w - dx
        if (newX >= 0 && newW >= this.minCropPx) {
          this.cropBox.x = newX
          this.cropBox.w = newW
        }
        break
      }
      case "right": {
        const newW = this.cropBox.w + dx
        if (newW >= this.minCropPx && this.cropBox.x + newW <= maxX) {
          this.cropBox.w = newW
        }
        break
      }
      case "topleft": {
        const newX = this.cropBox.x + dx
        const newW = this.cropBox.w - dx
        if (newX >= 0 && newW >= this.minCropPx) {
          this.cropBox.x = newX
          this.cropBox.w = newW
        }
        const newY = this.cropBox.y + dy
        const newH = this.cropBox.h - dy
        if (newY >= 0 && newH >= this.minCropPx) {
          this.cropBox.y = newY
          this.cropBox.h = newH
        }
        break
      }
      case "topright": {
        const newW = this.cropBox.w + dx
        if (newW >= this.minCropPx && this.cropBox.x + newW <= maxX) {
          this.cropBox.w = newW
        }
        const newY = this.cropBox.y + dy
        const newH = this.cropBox.h - dy
        if (newY >= 0 && newH >= this.minCropPx) {
          this.cropBox.y = newY
          this.cropBox.h = newH
        }
        break
      }
      case "bottomleft": {
        const newX = this.cropBox.x + dx
        const newW = this.cropBox.w - dx
        if (newX >= 0 && newW >= this.minCropPx) {
          this.cropBox.x = newX
          this.cropBox.w = newW
        }
        const newH = this.cropBox.h + dy
        if (newH >= this.minCropPx && this.cropBox.y + newH <= maxY) {
          this.cropBox.h = newH
        }
        break
      }
      case "bottomright": {
        const newW = this.cropBox.w + dx
        if (newW >= this.minCropPx && this.cropBox.x + newW <= maxX) {
          this.cropBox.w = newW
        }
        const newH = this.cropBox.h + dy
        if (newH >= this.minCropPx && this.cropBox.y + newH <= maxY) {
          this.cropBox.h = newH
        }
        break
      }
    }

    if (isCornerHandle && this.cropShape !== "Freeform") {
      this.enforceAspectRatio(this.activeCropHandle as "topleft" | "topright" | "bottomleft" | "bottomright", maxX, maxY)
    }
  }

  private enforceAspectRatio(handle: "topleft" | "topright" | "bottomleft" | "bottomright", maxX: number, maxY: number): void {
    const { x, y, w, h } = this.cropBox
    const ratio = this.resolveRatio()
    let newW = Math.max(this.minCropPx, w)
    let newH = Math.max(this.minCropPx, Math.round(newW / ratio))

    let newX = x
    let newY = y
    if (handle === "topleft") {
      newX = x + w - newW
      newY = y + h - newH
    } else if (handle === "topright") {
      newY = y + h - newH
    } else if (handle === "bottomleft") {
      newX = x + w - newW
    }

    newX = Math.max(0, Math.min(maxX - newW, newX))
    newY = Math.max(0, Math.min(maxY - newH, newY))

    const maxAllowedW = maxX - newX
    const maxAllowedH = maxY - newY

    newW = Math.min(newW, maxAllowedW)
    newH = Math.round(newW / ratio)
    if (newH > maxAllowedH) {
      newH = maxAllowedH
      newW = Math.round(newH * ratio)
    }

    this.cropBox = { x: newX, y: newY, w: newW, h: newH }
  }

  @HostListener("document:mouseup")
  onMouseUp(): void {
    this.activeCropHandle = null
    this.cropDragStart = null
    this.isFillDragging = false
    this.fillDragStart = null
  }

  private buildHDImage(): HDImage {
    const rawCropping = this.workingImage?.imgCropping ?? this.workingImage?.imageCropping
    const cropping = rawCropping ? (typeof rawCropping === "string" ? JSON.parse(rawCropping) : rawCropping) : null
    return {
      imageCropping: cropping,
      imageLink: this.workingImage?.img,
      linkType: this.image?.linkType ?? "",
      link: this.image?.link ?? "",
      subpageLink: this.image?.subpageLink,
      linkSubType: this.image?.linkSubType,
      priority: this.image?.priority ?? 0,
      objType: this.image?.objType ?? "",
    }
  }

  save(): void {
    const hdImage = this.buildHDImage()

    if (this.imageFit === "Original" && this.imageRotation === 0) {
      const originalLink = hdImage.imageCropping?.oldImageLink ?? hdImage.imageLink
      hdImage.imageLink = originalLink
      hdImage.imageCropping = null
      this.applyWorkingImageToSource()
      this.saved.emit(hdImage)
      return
    }

    this.uploadCroppedImageToBlob(hdImage)
  }

  private applyWorkingImageToSource(): void {
    Object.assign(this.image.inlineStyle, this.workingImage.inlineStyle)
    this.image.img = this.workingImage.img
    this.image.imgAspectRatio = this.workingImage.imgAspectRatio
    this.image.imgCropping = this.workingImage.imgCropping
  }

  cancel(): void {
    Object.assign(this.image.inlineStyle, this.savedInlineStyle)
    this.cancelled.emit()
  }

  private computeCropPx(realW: number, realH: number): { left: number; top: number; right: number; bottom: number } {
    if (this.imageFit === "Fill") {
      const scaleX = realW / this.renderedWidth
      const scaleY = realH / this.renderedHeight
      const displayW = this.sizeInputs[0].value
      const displayH = this.sizeInputs[1].value
      const visibleWidth = displayW * scaleX
      const visibleHeight = displayH * scaleY
      const rawLeft = ((this.renderedWidth - displayW) / 2 - this.fillDragX) * scaleX
      const rawTop = ((this.renderedHeight - displayH) / 2 - this.fillDragY) * scaleY
      const left = Math.max(0, Math.min(realW - visibleWidth, rawLeft))
      const top = Math.max(0, Math.min(realH - visibleHeight, rawTop))
      return { left, top, right: Math.max(0, realW - left - visibleWidth), bottom: Math.max(0, realH - top - visibleHeight) }
    }

    const scaleX = realW / this.renderedWidth
    const scaleY = realH / this.renderedHeight
    const left = this.cropBox.x * scaleX
    const top = this.cropBox.y * scaleY
    const right = realW - left - this.cropBox.w * scaleX
    const bottom = realH - top - this.cropBox.h * scaleY
    return { left, top, right, bottom }
  }

  private uploadCroppedImageToBlob(hdImage: HDImage): void {
    if (!this.imageSrc) return this.handleSaveError("No image source to save")
    this.uploading = true
    this.saveError.set(null)

    const target = this.folderTarget()
    const cacheKey = `${this.hotelId}:${target.id}`
    const folderIdPromise = this.resolveFolderId(cacheKey, target)
    folderIdPromise.catch(() => undefined)
    const sourceImage = (this.sourceImagePromise ?? Promise.reject()).catch(() => this.loadSourceImage(this.imageSrc))

    sourceImage
      .then((img) => this.renderCroppedImage(img))
      .then((rendered) => folderIdPromise.then((folderId) => this.uploadRenderedImage(hdImage, rendered, folderId, cacheKey)))
      .catch((err) => this.handleSaveError(err?.message ?? "Saving adjusted image failed", err))
  }

  private loadSourceImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = "anonymous"
      img.onload = () => resolve(img)
      img.onerror = () => reject(new Error("Failed to load original image"))
      img.src = src
    })
  }

  private renderCroppedImage(img: HTMLImageElement): Promise<RenderedCrop> {
    const isTransposed = this.imageRotation === 90 || this.imageRotation === 270
    const rotCanvas = document.createElement("canvas")
    rotCanvas.width = isTransposed ? img.height : img.width
    rotCanvas.height = isTransposed ? img.width : img.height
    const rotCtx = rotCanvas.getContext("2d")
    if (!rotCtx) return Promise.reject(new Error("Canvas context could not be created"))
    rotCtx.translate(rotCanvas.width / 2, rotCanvas.height / 2)
    rotCtx.rotate((this.imageRotation * Math.PI) / 180)
    rotCtx.drawImage(img, -img.width / 2, -img.height / 2)
    rotCtx.resetTransform()

    const crop = this.computeCropPx(rotCanvas.width, rotCanvas.height)
    const cropWidth = Math.max(1, rotCanvas.width - crop.left - crop.right)
    const cropHeight = Math.max(1, rotCanvas.height - crop.top - crop.bottom)
    const outputScale = Math.min(1, this.MAX_OUTPUT_SIDE / Math.max(cropWidth, cropHeight))

    const canvas = document.createElement("canvas")
    canvas.width = Math.max(1, Math.round(cropWidth * outputScale))
    canvas.height = Math.max(1, Math.round(cropHeight * outputScale))
    const ctx = canvas.getContext("2d")
    if (!ctx) return Promise.reject(new Error("Canvas context could not be created"))
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = "high"
    ctx.drawImage(rotCanvas, crop.left, crop.top, cropWidth, cropHeight, 0, 0, canvas.width, canvas.height)

    const hasTransparency = this.hasMeaningfulAlpha(ctx, canvas.width, canvas.height)
    const imageType = hasTransparency ? "image/png" : "image/jpeg"
    const fileExtension = hasTransparency ? "png" : "jpg"

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error("Failed to create blob from canvas"))
          resolve({ blob, imageType, fileExtension, crop, width: Math.round(cropWidth), height: Math.round(cropHeight) })
        },
        imageType,
        imageType === "image/jpeg" ? this.JPEG_QUALITY : undefined,
      )
    })
  }

  private hasMeaningfulAlpha(ctx: CanvasRenderingContext2D, width: number, height: number): boolean {
    const imageData = ctx.getImageData(0, 0, width, height).data
    const threshold = (imageData.length / 4) * this.MEANINGFUL_ALPHA_RATIO
    let transparentPixels = 0
    for (let i = 3; i < imageData.length; i += 4) {
      if (imageData[i] < 250 && ++transparentPixels > threshold) return true
    }
    return false
  }

  private folderTarget(): { id: string; name: string } {
    if (this.isEmailBuilder) return { id: this.emailBuilderId, name: this.emailBuilderName }
    const hd: HotelDirectory | null = this.hdService.getHotelDirectory() ?? JSON.parse(localStorage.getItem("hotel-directory"))
    return { id: hd?.id, name: hd?.name }
  }

  private resolveFolderId(cacheKey: string, target: { id: string; name: string }): Promise<string> {
    const cached = AgAdjustImageComponent.folderIdCache.get(cacheKey)
    if (cached) return Promise.resolve(cached)
    const hotelId = this.hotelId
    return lastValueFrom(this.utilityApiService.MediaLibrary.getAllMedia(hotelId, true))
      .then((mediaList) => {
        if (!mediaList) throw new Error("Media library returned no data")
        const existingId = this.mediaLibraryCleanupService.findEntityFolder(mediaList, target.id)?.id
        if (existingId) return existingId
        return lastValueFrom(this.utilityApiService.MediaLibrary.createFolder(hotelId, hotelId, encodeURIComponent(target.name) + ":" + target.id)).then((media) => {
          if (!media) throw new Error("Media folder could not be created")
          return media.id
        })
      })
      .then((folderId) => {
        AgAdjustImageComponent.folderIdCache.set(cacheKey, folderId)
        return folderId
      })
  }

  private uploadRenderedImage(hdImage: HDImage, rendered: RenderedCrop, folderId: string, cacheKey: string): Promise<void> {
    const hotelId = this.hotelId
    const isReplacingCrop = !!hdImage.imageCropping?.oldImageLink
    const imageId = isReplacingCrop ? hdImage.imageCropping.imageId : uuidv4()
    const imageUrl = Environment.BlobStorageUrl + "/medialibrary/" + hotelId + "/" + imageId + "." + rendered.fileExtension + (isReplacingCrop ? "?date=" + Date.now() : "")

    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(new File([rendered.blob], `cropped-${imageId}.${rendered.fileExtension}`, { type: rendered.imageType }))

    return lastValueFrom(this.utilityApiService.MediaLibrary.uploadFile(hotelId, folderId, dataTransfer.files, null, imageId)).then(
      () => this.finalizeSave(hdImage, imageId, imageUrl, rendered.crop.left, rendered.crop.top, rendered.crop.bottom, rendered.crop.right, rendered.width, rendered.height),
      (err) => {
        AgAdjustImageComponent.folderIdCache.delete(cacheKey)
        throw err
      },
    )
  }

  private handleSaveError(context: string, error?: unknown): void {
    console.error(context, error)
    this.uploading = false
    this.dismissClickedOutside.emit()
    this.saveError.set("Couldn't save image - please try again or contact AeroGuest Support")
    this.saveErrorPulse.update((n) => n + 1)
    setTimeout(() => this.dismissSaveError(), 8000)
  }

  dismissSaveError(): void {
    this.saveError.set(null)
  }

  private finalizeSave(hdImage: HDImage, imageId: string, imageUrl: string, cropLeft: number, cropTop: number, cropBottom: number, cropRight: number, canvasWidth: number, canvasHeight: number): void {
    const oldImageLink = hdImage.imageCropping?.oldImageLink ?? hdImage.imageLink
    const storedW = this.imageFit === "Fill" ? this.renderedWidth : canvasWidth
    const storedH = this.imageFit === "Fill" ? this.renderedHeight : canvasHeight
    if (!hdImage.imageCropping?.oldImageLink) {
      hdImage.imageCropping = {
        oldImageLink,
        imageId,
        cropLeft,
        cropTop,
        cropBottom,
        cropRight,
        imageFit: this.imageFit,
        cropShape: this.cropShape,
        originalWidth: storedW,
        originalHeight: storedH,
        imageRotation: this.imageRotation,
      }
    } else {
      hdImage.imageCropping.cropLeft = cropLeft
      hdImage.imageCropping.cropTop = cropTop
      hdImage.imageCropping.cropBottom = cropBottom
      hdImage.imageCropping.cropRight = cropRight
      hdImage.imageCropping.imageFit = this.imageFit
      hdImage.imageCropping.cropShape = this.cropShape
      hdImage.imageCropping.originalWidth = storedW
      hdImage.imageCropping.originalHeight = storedH
      hdImage.imageCropping.imageRotation = this.imageRotation
    }

    const style = this.workingImage?.inlineStyle
    if (style) {
      style.imageSizeIndependent = false
      const newLayoutMax = this.baseLayoutMax - (parseFloat(style.marginLeft) || 0) - (parseFloat(style.marginRight) || 0)
      style.layoutMax = newLayoutMax.toString()
    }

    hdImage.imageLink = imageUrl
    this.uploading = false
    this.applyWorkingImageToSource()
    this.saved.emit(hdImage)
  }
}
